import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { User } from '../contexts/AuthContext';

interface ClientDashboardProps {
  user: User;
}

interface ClientStats {
  auditorias: {
    completadas: number;
    enProceso: number;
    pendientes: number;
    totalFacturado: number;
  };
  finanzas: {
    totalDeuda: number;
    deudaPendiente: number;
    totalPagado: number;
    ultimoPago: string | null;
  };
  grupo: {
    esAdministrador: boolean;
    clientesAdministrados: any[];
    nombreGrupo: string | null;
  };
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ user }) => {
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [auditorias, setAuditorias] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientesDetalles, setClientesDetalles] = useState<any[]>([]);
  const [editingAudit, setEditingAudit] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [user.id, selectedClientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);

      // Cargar stats del cliente
      const statsResponse = await api.get(`/users/${user.id}/dashboard`);
      if (statsResponse.data.success && statsResponse.data.data.dashboard.tipo === 'cliente') {
        const dashboardData = statsResponse.data.data.dashboard;
        setStats(dashboardData);

        // Si es administrador de grupo, cargar detalles de los clientes
        if (dashboardData.grupo?.esAdministrador && dashboardData.grupo.clientesAdministrados?.length > 0) {
          const clientesIds = dashboardData.grupo.clientesAdministrados;
          console.log('📋 [ClientDashboard] Admin de grupo detectado, cargando clientes:', clientesIds);
          const clientesPromises = clientesIds.map((clientId: number) =>
            api.get(`/clients/${clientId}`).catch((err) => {
              console.error(`❌ Error cargando cliente ${clientId}:`, err);
              return null;
            })
          );
          const clientesResponses = await Promise.all(clientesPromises);
          console.log('📋 [ClientDashboard] Respuestas de clientes:', clientesResponses.length);
          const clientesData = clientesResponses
            .filter(resp => resp?.data?.success)
            .map(resp => resp.data.data);
          console.log('✅ [ClientDashboard] Clientes cargados exitosamente:', clientesData.length);
          setClientesDetalles(clientesData);
        }
      }

      // Cargar auditorías del cliente
      if (selectedClientId) {
        // Si hay un cliente seleccionado, cargar sus auditorías
        const auditsResponse = await api.get(`/clients/${selectedClientId}/audits`);
        if (auditsResponse.data.success) {
          setAuditorias(auditsResponse.data.data);
        }
      } else if (user.role === 'cliente' && user.clienteId) {
        // Si el usuario tiene clienteId asignado, usar ese
        const auditsResponse = await api.get(`/clients/${user.clienteId}/audits`);
        if (auditsResponse.data.success) {
          setAuditorias(auditsResponse.data.data);
        }
      } else if (statsResponse.data.success && statsResponse.data.data.dashboard.tipo === 'cliente' && !statsResponse.data.data.dashboard.grupo?.esAdministrador) {
        // Si no es admin de grupo, usar el ID del usuario como clienteId (formato legacy)
        const auditsResponse = await api.get(`/clients/${user.id}/audits`);
        if (auditsResponse.data.success) {
          setAuditorias(auditsResponse.data.data);
        }
      }

    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const getEstadoColor = (estado: string) => {
    const colores = {
      'pendiente': '#f59e0b',
      'asignada': '#3b82f6',
      'en_proceso': '#10b981',
      'pausada': '#ef4444',
      'terminada': '#8b5cf6',
      'entregada': '#059669'
    };
    return colores[estado as keyof typeof colores] || '#6b7280';
  };

  const handleDeleteAudit = async (auditId: number) => {
    if (!window.confirm('¿Estás seguro de eliminar esta auditoría? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await api.delete(`/audits/${auditId}`);
      alert('Auditoría eliminada exitosamente');
      loadClientData();
    } catch (error: any) {
      console.error('Error eliminando auditoría:', error);
      alert('Error al eliminar la auditoría: ' + (error.response?.data?.error || error.message));
    }
  };

  // Separar auditorías en pedidos y recibidas
  const misPedidos = auditorias.filter(a => ['pendiente', 'asignada', 'en_proceso', 'pausada'].includes(a.estado));
  const recibidas = auditorias.filter(a => ['terminada', 'entregada'].includes(a.estado));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner" />
        <p>Cargando dashboard del cliente...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
            Dashboard del Cliente
          </h1>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            Vista completa de tus auditorías y estado financiero
          </p>
        </div>
        {selectedClientId && stats?.grupo?.esAdministrador && (
          <button
            onClick={() => setSelectedClientId(null)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            ← Volver a Vista General
          </button>
        )}
      </div>

      {/* Estadísticas rápidas */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div className="card">
            <h3 style={{ color: '#3b82f6', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
              📋 Auditorías
            </h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {stats.auditorias.completadas}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Completadas • {stats.auditorias.enProceso} en proceso • {stats.auditorias.pendientes} pendientes
            </div>
          </div>

          <div className="card">
            <h3 style={{ color: '#059669', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
              💰 Finanzas
            </h3>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#dc2626' }}>
              {formatMoney(stats.finanzas.deudaPendiente)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Deuda pendiente • Total pagado: {formatMoney(stats.finanzas.totalPagado)}
            </div>
          </div>

          <div className="card">
            <h3 style={{ color: '#8b5cf6', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
              📊 Total Facturado
            </h3>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
              {formatMoney(stats.auditorias.totalFacturado)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Total de todas las auditorías
            </div>
          </div>

          {stats.grupo.esAdministrador && (
            <div className="card">
              <h3 style={{ color: '#f59e0b', fontSize: '1rem', margin: '0 0 0.5rem 0' }}>
                👥 Administrador de Grupo
              </h3>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#d97706' }}>
                {stats.grupo.clientesAdministrados.length} clientes
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {stats.grupo.nombreGrupo || 'Grupo sin nombre'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lista de clientes administrados (solo si es admin de grupo y no hay cliente seleccionado) */}
      {stats?.grupo?.esAdministrador && !selectedClientId && clientesDetalles.length > 0 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            🏢 Clientes que Administras
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem' }}>
            Haz clic en un cliente para ver sus auditorías y sucursales
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            {clientesDetalles.map((cliente) => (
              <div
                key={cliente.id}
                onClick={() => setSelectedClientId(cliente.id)}
                style={{
                  padding: '1.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: '#f9fafb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
              >
                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: '#1f2937' }}>
                  {cliente.nombre}
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  📧 {cliente.email}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                  👤 {cliente.contactoPrincipal}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#3b82f6', fontWeight: '500' }}>
                  {cliente.branches?.length || 0} sucursal(es)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mis Pedidos (auditorías pendientes/asignadas que el cliente cargó) */}
      {stats && (stats.grupo?.esAdministrador ? selectedClientId : true) && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            📤 {selectedClientId ? 'Pedidos del Cliente' : 'Mis Pedidos'}
          </h3>

          {misPedidos.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
              No hay pedidos pendientes
            </p>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                    Cliente
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                    Fecha
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    Estado
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                    Monto
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {misPedidos.map((auditoria, index) => (
                  <tr key={auditoria.id} style={{
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                  }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                      {auditoria.client?.nombre || 'Cliente no asignado'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {new Date(auditoria.createdAt).toLocaleDateString('es-AR')}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: '#f59e0b20',
                        color: '#f59e0b'
                      }}>
                        Pendiente
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'right',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {auditoria.gratisParaCliente ? (
                        <span style={{ color: '#10b981' }}>GRATIS</span>
                      ) : (
                        formatMoney(auditoria.costoFinal)
                      )}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                    }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {auditoria.estado === 'pendiente' ? (
                          <button
                            onClick={() => handleDeleteAudit(auditoria.id)}
                            style={{
                              padding: '0.25rem 0.75rem',
                              fontSize: '0.75rem',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer'
                            }}
                            title="Eliminar auditoría"
                          >
                            🗑️ Eliminar
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            -
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* Auditorías Recibidas (terminadas/entregadas) */}
      {stats && (stats.grupo?.esAdministrador ? selectedClientId : true) && (
        <div className="card">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            📥 {selectedClientId ? 'Auditorías Recibidas del Cliente' : 'Auditorías Recibidas'}
          </h3>

          {recibidas.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
              No hay auditorías recibidas
            </p>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                    Cliente
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                    Fecha Entrega
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    Estado
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                    Monto
                  </th>
                </tr>
              </thead>
              <tbody>
                {recibidas.map((auditoria, index) => (
                  <tr key={auditoria.id} style={{
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                  }}>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                      {auditoria.client?.nombre || 'Cliente no asignado'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      {auditoria.fechaTerminacion
                        ? new Date(auditoria.fechaTerminacion).toLocaleDateString('es-AR')
                        : new Date(auditoria.createdAt).toLocaleDateString('es-AR')
                      }
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: getEstadoColor(auditoria.estado) + '20',
                        color: getEstadoColor(auditoria.estado)
                      }}>
                        {auditoria.estado === 'terminada' ? 'Terminada' : 'Entregada'}
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'right',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {auditoria.gratisParaCliente ? (
                        <span style={{ color: '#10b981' }}>GRATIS</span>
                      ) : (
                        formatMoney(auditoria.costoFinal)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* Estilos */}
      <style jsx>{`
        .card {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .spinner {
          border: 3px solid #f3f4f6;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ClientDashboard;